/**
 * EarningsCard.tsx — the clinician's OWN "My earnings" card (2026-08-26).
 *
 * Owner repo: @thegpservice/gpsclinics-portal. Slice: earnings insights.
 *
 * MONEY POSTURE (the CTO decision of 2026-08-26): a clinician MAY see their
 * OWN earnings — their compensation, which is company money owed to THEM, and
 * therefore their own employment data. The long-standing "clinicians never see
 * money" rule continues to cover PATIENT money (prices, bills, refunds) and
 * every OTHER clinician's figures: none of that renders here or anywhere else
 * on /clinicians, and the company total + per-clinician table stay on the
 * ADMIN Insights surface. This card is therefore the ONE deliberate money
 * element on the clinician surface, and the money-redaction sweep in
 * clinicians.spec.tsx excludes exactly this card and nothing else.
 *
 * VIEW ONLY (2026-09-06). The card shows the same six activity counts as the
 * ADMIN table, the rate card those counts were paid at, and the earned total.
 * It offers NO way to change any of it — no rate inputs, no ⋯ menu, no dialog.
 * Only an ADMIN sets a rate (admin/EarningsTab.tsx), and the server enforces
 * that regardless: this file simply never renders an affordance that would
 * imply otherwise. `earnings.spec.tsx` pins the absence.
 *
 * WHY IT IS THE ADMIN TABLE'S GRAMMAR AND NOT A TILE GRID: the two surfaces
 * describe the SAME six measures, and a clinician querying their pay reads
 * their own figures alongside an admin reading the table. Identical column
 * names, order, right-aligned tabular numerals and £ formatting mean the two
 * screens can be held side by side without anyone having to translate.
 * Consequently "New patient repeats" is now a permanent column rather than a
 * tile that appeared only when non-zero: the old conditional existed because a
 * lone "0" tile beside five live ones reads as a broken figure, and that
 * concern does not survive the move — a 0 in a column of numbers reads as
 * "none this period", which is the honest answer.
 *
 * R41 (affordance-only): the card reads GET /v1/orders/clinician/earnings/me
 * and never sends a clinician ref — the server derives the clinician from the
 * session and enforces the role; the UI merely renders the answer.
 *
 * PERIODS: the same month picker as the admin Earnings panel (this month to
 * date by default; past 12 calendar months; custom day range) serialising the
 * identical half-open UTC `?from=&to=` (lib/monthRange). Fail-soft: the
 * server's `partial: true` (clinic callback figures unreachable) renders the
 * honest "totals may be low" note rather than a silently-short number. Money
 * renders from integer pence through the app money formatter.
 */
import { useCallback, useEffect, useId, useState } from 'react';
import { getMyEarnings } from '../api/orders';
import type { ClinicianEarnings, MyEarningsResponse } from '../api/types';
import { formatPence } from '../lib/format';
import type { EarningsRange } from '../lib/monthRange';
import { SectionTitle } from '../staff/ui';
import { DataTable, Td, Th } from '../ui/Table';
import { MonthRangePicker, defaultEarningsRange } from '../ui/MonthRangePicker';
import { Alert, Card, ErrorState, Spinner, tableCellStyle } from '../ui/kit';

/**
 * The six measures, in the ADMIN table's column order, each paired with the
 * rate key that pays for it.
 *
 * Deliberately declared here rather than imported from admin/EarningsTab: the
 * clinician bundle must not reach into the ADMIN money surface for a constant,
 * and there is no shared earnings module to hang it on yet. The duplication is
 * the cheap half of that trade — if the vocabulary ever diverges, the two
 * spec files disagree loudly rather than silently.
 */
const MEASURES: Array<{
  heading: string;
  count: keyof Pick<ClinicianEarnings, 'callbacksHandled' | 'convertedCallbacks' | 'approvedOrders' | 'dispatchedOrders' | 'repeatPrescriptions' | 'newPatientRepeats'>;
  rate: keyof ClinicianEarnings['rates'];
}> = [
  { heading: 'Callbacks', count: 'callbacksHandled', rate: 'perCallbackPence' },
  { heading: 'Converted', count: 'convertedCallbacks', rate: 'perConvertedCallbackPence' },
  { heading: 'Forms approved', count: 'approvedOrders', rate: 'perApprovedOrderPence' },
  { heading: 'Deliveries', count: 'dispatchedOrders', rate: 'perDispatchPence' },
  { heading: 'Repeat months', count: 'repeatPrescriptions', rate: 'perRepeatRxPence' },
  { heading: 'New patient repeats', count: 'newPatientRepeats', rate: 'perNewPatientRepeatPence' },
];

/** The one measure whose rule is not guessable from its column name. */
const NEW_PATIENT_RULE =
  'New patient repeats: a one-off, paid the first time a new patient gets a repeat prescription from their initial form.';

/**
 * THE ESTIMATE (CTO 2026-09-09). The approval fee is earned by whoever gives
 * the FINAL approval before the patient's card is charged, so a plan you have
 * recommended earns nothing until the patient pays — but the work is done and
 * the fee is expected "until the patient requests something else". The server
 * counts your plans still waiting on the patient and prices them at YOUR
 * approval rate; this card shows that as its own figure, beside Earned and
 * never inside it. Same wording posture as NEW_PATIENT_RULE: a visible
 * sentence, tied to the cells it explains.
 */
const ESTIMATE_RULE =
  'Estimated is as of today, not the period above: everything you have recommended that the patient has not paid for yet, ' +
  'whenever you recommended it, priced at your approval rate. It becomes earned when the patient pays and the final approval is given, ' +
  'and changes if the patient asks for something else. It is shown beside your earned figures and is never added to them.';

/**
 * THE ESTIMATE PAIR, RENDERED SAFELY — "—" when the server did not send it.
 *
 * ⚠️ NOT COSMETIC, AND NOT `?? 0`. The types declare `plansAwaitingPayment` and
 * `estimatedPence` as required, but TypeScript cannot enforce a wire contract:
 * a portal running against an orders-service that predates the estimate (a
 * rollback, or a release that lands out of order) receives neither, and
 * `formatPence(undefined)` renders the literal **£NaN** on a money screen —
 * which is what this guard exists to stop. It was live for one deploy: an
 * incomplete inline test fixture hid it behind a React warning nobody read.
 *
 * A zero fallback is the tempting fix and is WORSE: "£0" is indistinguishable
 * from "nothing is outstanding", which is precisely the misreading the
 * as-of-today rule was introduced to end. "—" says we were not told, which is
 * the truth, and it can never be mistaken for a figure.
 */
const estimateText = (value: number | undefined, render: (n: number) => string): string =>
  typeof value === 'number' && Number.isFinite(value) ? render(value) : '—';

const numericCellStyle = { textAlign: 'right' as const, fontVariantNumeric: 'tabular-nums' as const };
/** The row-label column: a real `<th scope="row">` on the shared cell style. */
const rowHeadStyle = { ...tableCellStyle, textAlign: 'left' as const, fontWeight: 600, whiteSpace: 'nowrap' as const };

export function EarningsCard() {
  // This month to date until the picker says otherwise — same default as the
  // admin panel, so both surfaces answer "this month" out of the box.
  const [range, setRange] = useState<EarningsRange>(() => defaultEarningsRange(new Date()));
  const [data, setData] = useState<MyEarningsResponse | null>(null);
  const [status, setStatus] = useState<'loading' | 'ready' | 'error'>('loading');
  const ruleId = useId();
  const estimateRuleId = useId();
  // Muted on purpose: a forecast must never read with the weight of money owed.
  const estimateCellStyle = { ...numericCellStyle, color: 'var(--gps-muted)' };

  const load = useCallback((window: EarningsRange) => {
    setStatus('loading');
    getMyEarnings(window)
      .then((res) => {
        setData(res);
        setStatus('ready');
      })
      .catch(() => setStatus('error'));
  }, []);

  useEffect(() => {
    load(range);
  }, [load, range]);

  return (
    // A labelled section: the money-redaction sweep excises exactly this
    // landmark before asserting the REST of the surface stays money-free.
    <section aria-label="My earnings">
      <Card>
        <SectionTitle>My earnings</SectionTitle>
        <div style={{ display: 'flex', gap: 'var(--gps-s-2)', alignItems: 'center', marginBottom: 'var(--gps-s-3)', flexWrap: 'wrap' }}>
          <MonthRangePicker idBase="my-earnings-period" onRange={setRange} />
          {data && status === 'ready' ? (
            <span style={{ marginLeft: 'auto', fontSize: '14px', fontWeight: 700, color: 'var(--gps-text)' }}>
              Earned: {formatPence(data.me.earnedPence)}
            </span>
          ) : null}
          {data && status === 'ready' ? (
            // The forecast BESIDE the earned figure — its own label, muted,
            // never added into the number to its left.
            <span style={{ fontSize: '13px', fontWeight: 600, color: 'var(--gps-muted)' }}>
              Estimated now: {estimateText(data.me.estimatedPence, formatPence)}
            </span>
          ) : null}
        </div>

        {status === 'loading' ? (
          <Spinner label="Loading your earnings…" />
        ) : status === 'error' || !data ? (
          <ErrorState message="Sorry — we could not load your earnings." onRetry={() => load(range)} />
        ) : (
          <>
            {data.partial ? (
              <Alert tone="warning" role="status" style={{ marginBottom: 'var(--gps-s-3)' }}>
                {/* Same correction as the admin panel: the estimate rides the
                    same clinic read, so it must not sit under a reassurance. */}
                Callback figures are temporarily unavailable. Your callbacks and converted are counted as zero, so your
                earnings are understated, and your estimate cannot be worked out — it shows as “—”. Your order figures
                (forms approved, deliveries, repeat months, new patient repeats) are still exact.
              </Alert>
            ) : null}

            <DataTable
              label="My earnings breakdown"
              head={
                <>
                  {/* The row-label column. Named for screen readers only — an
                      empty header cell fails axe's empty-table-header rule. */}
                  <Th>
                    <span className="gps-sr-only">Figure</span>
                  </Th>
                  {MEASURES.map((m) => (
                    <Th key={m.count} style={{ textAlign: 'right' }}>
                      {m.heading}
                    </Th>
                  ))}
                  <Th style={{ textAlign: 'right' }}>Earned</Th>
                  {/* The estimate pair — AFTER Earned, in the ADMIN table's
                      exact position, so the six paid measures read as before
                      and the forecast is visibly separate (ESTIMATE_RULE).
                      ⚠️ Unlike every other column here these two do NOT move
                      with the period picker — they are what is outstanding
                      TODAY (CTO 2026-09-09), which the rule sentence states. */}
                  {/* "(now)" — every OTHER column is the period named in the
                      picker above; these two are as of today. */}
                  <Th style={{ textAlign: 'right' }}>Awaiting payment (now)</Th>
                  <Th style={{ textAlign: 'right' }}>Estimated (now)</Th>
                </>
              }
            >
              <tr>
                <th scope="row" style={rowHeadStyle}>
                  Count
                </th>
                {MEASURES.map((m) => (
                  <Td key={m.count} style={numericCellStyle}>
                    {data.me[m.count]}
                  </Td>
                ))}
                <Td style={{ ...numericCellStyle, fontWeight: 700, whiteSpace: 'nowrap' }}>{formatPence(data.me.earnedPence)}</Td>
                <Td style={estimateCellStyle}>{estimateText(data.me.plansAwaitingPayment, String)}</Td>
                <Td style={{ ...estimateCellStyle, whiteSpace: 'nowrap' }}>{estimateText(data.me.estimatedPence, formatPence)}</Td>
              </tr>
              {/* THE CLINICIAN'S OWN RATE CARD — readable, never editable. It
                  is what makes the earned figure checkable by the person being
                  paid, which is the whole point of the 2026-08-26 carve-out. */}
              <tr>
                <th scope="row" style={rowHeadStyle}>
                  Your rate
                </th>
                {MEASURES.map((m) => (
                  // A RAW <td>, not the shared <Td>, and deliberately so.
                  // ui/Table's Td takes only {children, style, colSpan} and
                  // spreads nothing else — and TypeScript exempts hyphenated
                  // JSX attribute names from its excess-property check, so
                  // `aria-describedby` on a <Td> compiles clean and then never
                  // reaches the DOM. That is a silent accessibility regression
                  // with a green build behind it, which is why this cell is
                  // built by hand on the same shared cell style.
                  <td
                    key={m.rate}
                    style={{ ...tableCellStyle, ...numericCellStyle }}
                    // The one-off's rule is written out below the table and
                    // tied here, so a screen-reader user meets it on the cell
                    // it explains. Visible text, never a title tooltip: a
                    // tooltip survives neither a print nor a keyboard read.
                    aria-describedby={m.count === 'newPatientRepeats' ? ruleId : undefined}
                  >
                    {formatPence(data.me.rates[m.rate])}
                  </td>
                ))}
                <Td />
                {/* The rate the estimate is priced at — YOUR approval rate,
                    the same one under "Forms approved", because it is the same
                    fee before and after the patient pays. Hand-built <td> for
                    the same aria-describedby reason as the cells above. */}
                <td style={{ ...tableCellStyle, ...estimateCellStyle }} aria-describedby={estimateRuleId}>
                  {formatPence(data.me.rates.perApprovedOrderPence)}
                </td>
                <Td />
              </tr>
            </DataTable>

            <p id={ruleId} style={{ margin: 'var(--gps-s-2) 0 0', fontSize: '12.5px', lineHeight: 1.45, color: 'var(--gps-muted)' }}>
              {NEW_PATIENT_RULE}
            </p>
            <p id={estimateRuleId} style={{ margin: 'var(--gps-s-1) 0 0', fontSize: '12.5px', lineHeight: 1.45, color: 'var(--gps-muted)' }}>
              {ESTIMATE_RULE}
            </p>
          </>
        )}
      </Card>
    </section>
  );
}
